/**
 * Action-row prev/next capsule visibility. Extracted from
 * articleController so that file stays under the architecture
 * governance line cap. The function paints both the bottom
 * navigation cards and the action-row #articlePrevBtn /
 * #articleNextBtn hidden attribute from a single source of
 * truth (the cached Topic navigation record).
 */
import { byId } from "./domUtils";
import { getTopic, type TopicRecord } from "./topicStore";
import type { Root } from "./prototypeTypes";

/**
 * The five transport keys are a constant row: an unavailable key keeps its
 * slot and reads as disabled (same grammar as the boundary prev/next) rather
 * than collapsing the row. Only the loading skeleton may remove keys.
 */
export function paintMediaButtonAvailability(button: HTMLElement | null, available: boolean): void {
  if (!button) return;
  button.removeAttribute("hidden");
  if (available) {
    button.classList.remove('is-disabled');
    button.removeAttribute('aria-disabled');
    button.removeAttribute('tabindex');
    return;
  }
  button.classList.add('is-disabled');
  button.setAttribute('aria-disabled', 'true');
  button.setAttribute('tabindex', '-1');
}

function paintChapterLink(link: HTMLElement | null, slug: string, chapterIndex: number | null): void {
  if (!link) return;
  if (chapterIndex === null) {
    link.removeAttribute("href");
    link.setAttribute('aria-disabled', 'true');
    link.setAttribute('tabindex', '-1');
    link.classList.add('is-disabled');
    link.removeAttribute("hidden");
    return;
  }
  link.setAttribute("href", `/topics/${encodeURIComponent(slug)}/chapters/${chapterIndex}`);
  link.removeAttribute('aria-disabled');
  link.removeAttribute('tabindex');
  link.classList.remove('is-disabled');
  link.removeAttribute("hidden");
}

export function resetArticleNavigation(root: Root): void {
  const section = byId(root, "article-chapter-nav");
  if (section) section.hidden = true;
  paintChapterLink(byId(root, "articlePrevBtn"), "", null);
  paintChapterLink(byId(root, "articleNextBtn"), "", null);
  byId(root, "article-prev-list")?.replaceChildren();
  byId(root, "article-next-list")?.replaceChildren();
  const pair = byId(root, "article-nav-pair");
  if (pair) {
    pair.replaceChildren();
    pair.hidden = true;
  }
}

export function paintArticleNavVisibility(
  root: Root,
  slug: string,
  currentIndex: number,
): { prev: { index: number; title: string; duration?: string } | null; next: { index: number; title: string; duration?: string } | null } {
  const section = byId(root, "article-chapter-nav");
  const topic: TopicRecord | undefined = getTopic(slug);
  if (!section) return { prev: null, next: null };
  // Cold-cache path: getTopic() returned undefined because
  // ensureTopic() is still in flight. Do NOT touch DOM —
  // the caller in showArticle() will re-invoke us after the
  // index warms. Touching prev/next hidden here would race the
  // e2e waitForFunction and cause a one-frame flicker to hidden.
  if (!topic) return { prev: null, next: null };
  const chapters = [...topic.previewChapters, ...topic.lockedChapters].sort((a, b) => a.index - b.index);
  // prev = the chapter immediately BEFORE currentIndex (highest index < current).
  // The array is sorted ascending, so findLast gives the nearest predecessor.
  // (Previously .find returned the FIRST match = the smallest index, i.e.
  // chapter 1, making "Previous chapter" always point back to chapter 1.)
  const prev = chapters.findLast((chapter) => chapter.index < currentIndex) ?? null;
  const next = chapters.find((chapter) => chapter.index > currentIndex) ?? null;
  // The final chapter has a real Back to Topic card, so the navigation
  // section remains present even when the current directory has one chapter.
  section.hidden = false;
  const prevBtn = byId(root, "articlePrevBtn");
  const nextBtn = byId(root, "articleNextBtn");
  paintChapterLink(prevBtn, slug, prev?.index ?? null);
  paintChapterLink(nextBtn, slug, next?.index ?? null);
  paintChapterNavPair(root, slug, prev, next);
  return { prev, next };
}

const PAIR_PREV_ICON = '<svg aria-hidden="true" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m12 19-7-7 7-7"></path><path d="M19 12H5"></path></svg>';
const PAIR_LIST_ICON = '<svg aria-hidden="true" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M8 6h13"></path><path d="M8 12h13"></path><path d="M8 18h13"></path><path d="M3 6h.01"></path><path d="M3 12h.01"></path><path d="M3 18h.01"></path></svg>';

/**
 * Mobile bottom-navigation pair (prototype round-4): bordered Previous and
 * All chapters buttons under the outlined next-card. The final chapter drops
 * All chapters (the back-to-topic card carries it) and Previous stretches
 * full width via flex. Desktop keeps the two-card grid and never renders the
 * pair (CSS display:none outside the phone breakpoint).
 */
function paintChapterNavPair(
  root: Root,
  slug: string,
  prev: { index: number } | null,
  next: { index: number } | null,
): void {
  const pair = byId(root, "article-nav-pair");
  if (!pair) return;
  const prevHtml = prev
    ? `<a class="chapter-nav-pair-btn" href="/topics/${encodeURIComponent(slug)}/chapters/${prev.index}" data-proto-click="showArticle(${prev.index})">${PAIR_PREV_ICON}<span>Previous</span></a>`
    : "";
  const allHtml = next
    ? `<a class="chapter-nav-pair-btn" href="/topics/${encodeURIComponent(slug)}" data-proto-click="openChapterDirectory()">${PAIR_LIST_ICON}<span>All chapters</span></a>`
    : "";
  pair.innerHTML = prevHtml + allHtml;
  pair.hidden = !prevHtml && !allHtml;
}
